/**
 * File Validation Utilities
 *
 * Provides validation functions for file uploads including size and type checking.
 * Uses configuration from fileUploadConfig for validation rules.
 *
 * @module fileValidation
 */

import {
    getFileUploadConfig,
    formatFileSize,
    getFileCategory,
    getMaxFileSizeForFile,
} from "./fileUploadConfig";

/**
 * Result of a single file validation operation
 */
export interface FileValidationResult {
    /** Whether the file passed validation */
    isValid: boolean;
    /** Error message if validation failed */
    error?: string;
    /** The file that was validated */
    file: File;
}

/**
 * Summary of batch file validation
 */
export interface FileValidationSummary {
    /** Files that passed all validation checks */
    validFiles: File[];
    /** Files that failed validation with error details */
    invalidFiles: FileValidationResult[];
    /** Array of error messages for display */
    errors: string[];
}

/**
 * Validates file extension + MIME type against configured allow-lists.
 *
 * Returns null when file type is supported, otherwise returns a user-facing
 * validation message.
 */
export const validateFileType = (file: File): string | null => {
    const config = getFileUploadConfig();
    const extension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));
    const normalizedMimeType = (file.type || "").toLowerCase();

    // Audio uploads are explicitly unsupported across all flows.
    const unsupportedAudioExtensions = new Set([
        ".mp3",
        ".wav",
        ".m4a",
        ".aac",
        ".flac",
        ".ogg",
        ".oga",
        ".wma",
        ".weba",
        ".aiff",
        ".aif",
        ".alac",
    ]);
    const isAudioExtension = unsupportedAudioExtensions.has(extension);
    const isAudioMimeType = normalizedMimeType.startsWith("audio/");

    if (isAudioExtension || isAudioMimeType) {
        const supportedExtensions = config.supportedFileTypes.extensions
            .map((ext) => ext.toUpperCase().replace(".", ""))
            .join(", ");
        return `File "${file.name}" is not a supported file type. Supported formats: ${supportedExtensions}.`;
    }

    const isValidExtension =
        config.supportedFileTypes.extensions.includes(extension);
    const isValidMimeType =
        config.supportedFileTypes.mimeTypes.includes(normalizedMimeType);

    if (isValidExtension && isValidMimeType) {
        return null;
    }

    const supportedExtensions = config.supportedFileTypes.extensions
        .map((ext) => ext.toUpperCase().replace(".", ""))
        .join(", ");

    return `File "${file.name}" is not a supported file type. Supported formats: ${supportedExtensions}.`;
};

/**
 * Validates a single file against configured size and type constraints.
 *
 * Performs the following validation checks:
 * 1. File object is valid (has name and type)
 * 2. File extension is in allowed list
 * 3. File MIME type is in allowed list
 * 4. File size does not exceed configured maximum
 *
 * @param {File} file - The file to validate
 * @returns {FileValidationResult} Validation result with isValid flag and optional error message
 *
 * @example
 * ```typescript
 * const result = validateFile(myFile);
 * if (!result.isValid) {
 *   console.error(result.error);
 *   showToast({ message: result.error, variant: 'danger' });
 * }
 * ```
 */
export const validateFile = (file: File): FileValidationResult => {
    const config = getFileUploadConfig();

    // Validate file object integrity
    if (!file || !file.name || !file.type) {
        return {
            isValid: false,
            error: "Invalid file object detected. Please try selecting the file again.",
            file,
        };
    }

    // Validate file type before size checks to avoid misleading "size exceeded"
    // errors for unsupported formats.
    const fileTypeError = validateFileType(file);
    if (fileTypeError) {
        return {
            isValid: false,
            error: fileTypeError,
            file,
        };
    }

    // Validate file size based on category (Images: 10MB, Documents: 20MB, Videos: 25MB)
    const category = getFileCategory(file.name);
    const maxFileSize = getMaxFileSizeForFile(file.name);
    const categoryConfig =
        category === "image"
            ? { label: "Images", maxSizeMB: config.maxImageSizeMB }
            : category === "video"
              ? { label: "Media files", maxSizeMB: config.maxVideoSizeMB }
              : { label: "Documents", maxSizeMB: config.maxDocumentSizeMB };

    if (file.size > maxFileSize) {
        return {
            isValid: false,
            error: `File "${file.name}" exceeds the maximum allowed size for ${categoryConfig.label} (${categoryConfig.maxSizeMB}MB). Current size: ${formatFileSize(file.size)}. Please upload a smaller file.`,
            file,
        };
    }

    // File passed all validation checks
    return {
        isValid: true,
        file,
    };
};

/**
 * Validates multiple files in batch.
 *
 * Applies validateFile() to each file and returns a summary with valid and invalid files.
 * Useful for drag-and-drop scenarios where multiple files are selected at once.
 *
 * @param {FileList | File[]} files - FileList from input element or array of Files
 * @returns {FileValidationSummary} Summary containing valid files, invalid files, and error messages
 *
 * @example
 * ```typescript
 * const handleFileDrop = (event: React.DragEvent) => {
 *   const validation = validateFiles(event.dataTransfer.files);
 *
 *   if (validation.errors.length > 0) {
 *     showToast({ message: validation.errors.join('\n'), variant: 'danger' });
 *   }
 *
 *   // Process only valid files
 *   validation.validFiles.forEach(file => uploadFile(file));
 * };
 * ```
 */
export const validateFiles = (
    files: FileList | File[]
): FileValidationSummary => {
    const fileArray = Array.from(files);
    const results = fileArray.map(validateFile);

    return {
        validFiles: results.filter((r) => r.isValid).map((r) => r.file),
        invalidFiles: results.filter((r) => !r.isValid),
        errors: results.filter((r) => !r.isValid).map((r) => r.error!),
    };
};

type AttachmentNameReference = {
    fileName?: string | null;
    originalFileName?: string | null;
};

const SERVER_MANAGED_FILE_PREFIX_REGEX = /^\d+_\d{14,17}_(.+)$/;

const normalizeFileName = (fileName: string): string =>
    fileName.trim().toLowerCase();

const extractFileName = (value: string): string => {
    const withoutQuery = value.split("?")[0].split("#")[0];
    const segments = withoutQuery.split(/[\\/]/);
    return segments[segments.length - 1] || withoutQuery;
};

const stripServerManagedPrefix = (fileName: string): string => {
    const match = fileName.match(SERVER_MANAGED_FILE_PREFIX_REGEX);
    return match?.[1] || fileName;
};

export const getAttachmentNameCandidates = (
    attachment: AttachmentNameReference
): string[] => {
    const candidates = new Set<string>();

    [attachment.originalFileName, attachment.fileName].forEach((rawName) => {
        if (!rawName || typeof rawName !== "string") {
            return;
        }

        const extractedName = extractFileName(rawName);
        const normalizedName = normalizeFileName(extractedName);
        if (!normalizedName) {
            return;
        }

        candidates.add(normalizedName);

        const strippedName = stripServerManagedPrefix(extractedName);
        if (strippedName !== extractedName) {
            const normalizedStrippedName = normalizeFileName(strippedName);
            if (normalizedStrippedName) {
                candidates.add(normalizedStrippedName);
            }
        }
    });

    return Array.from(candidates);
};

export const doesAttachmentMatchFileName = (
    fileName: string,
    attachment: AttachmentNameReference,
    additionalFileNames: string[] = []
): boolean => {
    const targetNames = new Set<string>();

    [fileName, ...additionalFileNames].forEach((candidate) => {
        if (!candidate || typeof candidate !== "string") {
            return;
        }

        const extractedCandidate = extractFileName(candidate);
        const normalizedCandidate = normalizeFileName(extractedCandidate);
        if (normalizedCandidate) {
            targetNames.add(normalizedCandidate);
        }

        const strippedCandidate = stripServerManagedPrefix(extractedCandidate);
        if (strippedCandidate !== extractedCandidate) {
            const normalizedStrippedCandidate =
                normalizeFileName(strippedCandidate);
            if (normalizedStrippedCandidate) {
                targetNames.add(normalizedStrippedCandidate);
            }
        }
    });

    if (targetNames.size === 0) {
        return false;
    }

    return getAttachmentNameCandidates(attachment).some((nameCandidate) =>
        targetNames.has(nameCandidate)
    );
};

/**
 * Checks if a file is a duplicate of an existing attachment.
 *
 * A file is considered a duplicate if:
 * - File name matches (case-insensitive)
 * - File size is within tolerance (15% or 2KB, whichever is larger)
 *
 * The size tolerance accounts for potential metadata differences or re-encoding.
 *
 * @param {File} newFile - The file to check for duplication
 * @param {Array} existingAttachments - Array of existing attachments to check against
 * @returns {boolean} True if the file is a duplicate, false otherwise
 *
 * @example
 * ```typescript
 * const isDuplicate = isDuplicateFile(newFile, attachments);
 * if (isDuplicate) {
 *   const confirmed = confirm('This file already exists. Replace it?');
 *   if (confirmed) {
 *     replaceFile(newFile);
 *   }
 * }
 * ```
 */
export const isDuplicateFile = (
    newFile: File,
    existingAttachments: Array<{
        fileName: string;
        originalFileName?: string;
        size: number;
    }>
): boolean => {
    return existingAttachments.some((att) => {
        // Check if names match, including server-managed upload prefixes.
        const isSameName = doesAttachmentMatchFileName(newFile.name, att);

        // Calculate size tolerance (15% or 2KB, whichever is larger)
        const sizeDifference = Math.abs(att.size - newFile.size);
        const sizeTolerance = Math.max(att.size * 0.15, 2048);
        const isSizeClose = sizeDifference <= sizeTolerance;

        // File is duplicate if name matches AND size is close
        return isSameName && isSizeClose;
    });
};

/**
 * Validates that the total combined size of all attachments doesn't exceed the limit.
 *
 * @param {Array} attachments - Array of attachments (Files or existing attachments with size)
 * @returns {{isValid: boolean, totalSizeMB: number, error?: string}} Validation result
 *
 * @example
 * ```typescript
 * const result = validateTotalSize([...existingAttachments, ...newFiles]);
 * if (!result.isValid) {
 *   showError(result.error);
 * }
 * ```
 */
export const validateTotalSize = (
    attachments: Array<File | { size: number }>
): { isValid: boolean; totalSizeMB: number; error?: string } => {
    const config = getFileUploadConfig();
    const totalBytes = attachments.reduce((sum, item) => sum + item.size, 0);
    const totalMB = totalBytes / (1024 * 1024);

    if (totalMB > config.maxTotalSizeMB) {
        return {
            isValid: false,
            totalSizeMB: parseFloat(totalMB.toFixed(2)),
            error: `Total combined file size (${totalMB.toFixed(2)}MB) exceeds the maximum allowed (${config.maxTotalSizeMB}MB). Please remove some files or upload smaller files.`,
        };
    }

    return {
        isValid: true,
        totalSizeMB: parseFloat(totalMB.toFixed(2)),
    };
};
